// components/manage/EditRecordModal.tsx
import React, { useState, useEffect } from 'react';
import { DatabaseType } from '../../types';
import { dataManagementApi } from '../../services/dataManagementApi';
import {
  SDG_TITLES,
  EDUCATION_OPTIONS,
  ACTION_OPTIONS,
  COMMON_OPTIONS,
} from '../../../Form/types/collaboration';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseSdgString = (val: string | undefined): number[] => {
  if (!val) return [];
  return val
    .split(/[,;]/)
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !isNaN(n) && n >= 1 && n <= 17);
};

const sdgArrayToString = (arr: number[]): string =>
  [...arr].sort((a, b) => a - b).join(', ');

// ─── Field config types ───────────────────────────────────────────────────────

interface FieldConfig {
  key: string;
  label: string;
  type: 'input' | 'textarea' | 'select' | 'sdg_multiselect' | 'checkbox_multiselect';
  optionsList?: string[];
  separator?: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  options?: { value: string; label: string }[];
  hint?: string;
}

const toOpts = (items: (string | number)[]) =>
  items.map(i => ({ value: i.toString(), label: i.toString() }));

// ─── Field definitions ────────────────────────────────────────────────────────

const EDUCATION_FIELDS: FieldConfig[] = [
  { key: 'title',            label: 'Title',               type: 'input',           required: true, maxLength: 255 },
  { key: 'descriptions',     label: 'Description',         type: 'textarea',        required: true, maxLength: 1000 },
  { key: 'aims',             label: 'Aims',                type: 'textarea',        maxLength: 1000 },
  { key: 'learning_outcome', label: 'Learning Outcome',    type: 'textarea',        maxLength: 1000 },
  { key: 'sdgs_related',     label: 'SDGs Related',        type: 'sdg_multiselect', hint: 'Stored as comma-separated numbers e.g. 1, 3, 17' },
  { key: 'type_label',       label: 'Type Label',          type: 'input',           placeholder: 'e.g. Course, Event, Resource...', maxLength: 128 },
  { key: 'organization',     label: 'Organization',        type: 'input',           maxLength: 128 },
  {
    key: 'year', label: 'Year', type: 'select',
    options: [{ value: '', label: 'Select Year' }, ...toOpts(EDUCATION_OPTIONS.years)],
  },
  {
    key: 'location', label: 'Location', type: 'select',
    options: [{ value: '', label: 'Select Location' }, ...toOpts(COMMON_OPTIONS.regions)],
  },
  {
    key: 'discipline', label: 'Related Discipline', type: 'select',
    options: [{ value: '', label: 'Select Discipline' }, ...toOpts(EDUCATION_OPTIONS.disciplines)],
  },
  { key: 'industries', label: 'Useful Industries', type: 'checkbox_multiselect',
    optionsList: EDUCATION_OPTIONS.industries, separator: '; ' },
  { key: 'source', label: 'Source', type: 'textarea', maxLength: 500 },
  { key: 'link',   label: 'Link',   type: 'input',    placeholder: 'https://example.com', maxLength: 500 },
];

const ACTIONS_FIELDS: FieldConfig[] = [
  { key: 'actions',       label: 'Action',        type: 'textarea', required: true, maxLength: 1000 },
  { key: 'action_detail', label: 'Action Detail', type: 'textarea', required: true, maxLength: 1000 },
  { key: 'sdgs',          label: 'SDGs',          type: 'sdg_multiselect', hint: 'Stored as comma-separated numbers e.g. 1, 3, 17' },
  {
    key: 'level', label: 'Level', type: 'select',
    options: [{ value: '', label: 'Select Level' }, ...ACTION_OPTIONS.levels],
  },
  {
    key: 'individual_org', label: 'Individual / Organization', type: 'select',
    options: [{ value: '', label: 'Select...' }, ...ACTION_OPTIONS.individual_organization],
  },
  {
    key: 'location', label: 'Location', type: 'select',
    options: [{ value: '', label: 'Select Location' }, ...toOpts(COMMON_OPTIONS.regions)],
  },
  { key: 'industry', label: 'Related Industry', type: 'checkbox_multiselect',
    optionsList: ACTION_OPTIONS.industries, separator: '; ' },
  {
    key: 'digital_actions', label: 'Digital Actions', type: 'select',
    options: [{ value: '', label: 'Select...' }, ...ACTION_OPTIONS.digital_actions],
  },
  { key: 'source_desc',      label: 'Source Descriptions', type: 'textarea', maxLength: 1000 },
  { key: 'award_desc',       label: 'Award Descriptions',  type: 'textarea', maxLength: 1000 },
  {
    key: 'award', label: 'Award', type: 'select',
    options: [{ value: '', label: 'Select...' }, ...ACTION_OPTIONS.award],
  },
  { key: 'source_links',     label: 'Source Links',     type: 'input', placeholder: 'https://example.com', maxLength: 500 },
  { key: 'additional_notes', label: 'Additional Notes', type: 'textarea', maxLength: 1000 },
];

// ─── SDG multiselect sub-component ───────────────────────────────────────────

const SdgMultiSelect: React.FC<{
  value: string;
  onChange: (val: string) => void;
}> = ({ value, onChange }) => {
  const selected = parseSdgString(value);

  const toggle = (n: number) => {
    const next = selected.includes(n)
      ? selected.filter(x => x !== n)
      : [...selected, n];
    onChange(sdgArrayToString(next));
  };

  return (
    <div>
      <div className="border border-gray-300 rounded-lg p-3 max-h-52 overflow-y-auto grid grid-cols-2 gap-1">
        {SDG_TITLES.map((title, i) => {
          const num = i + 1;
          const checked = selected.includes(num);
          return (
            <label
              key={num}
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors select-none
                ${checked ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(num)}
                className="h-4 w-4 text-blue-600 rounded flex-shrink-0"
              />
              <span>SDG {num}: {title}</span>
            </label>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {selected.length > 0 ? `Selected: ${sdgArrayToString(selected)}` : 'None selected'}
      </p>
    </div>
  );
};

const CheckboxMultiSelect: React.FC<{
    options: string[];
    value: string;
    onChange: (val: string) => void;
    }> = ({ options, value, onChange }) => {
        const selected = value
        ? value.split(';').map(s => s.replace(/,/g, '').trim()).filter(Boolean)
        : [];
      
        const toggle = (item: string) => {
            const next = selected.includes(item)
            ? selected.filter(x => x !== item)
            : [...selected, item];
            onChange(next.join('; '));
        };
  
    return (
      <div>
        <div className="border border-gray-300 rounded-lg p-3 max-h-52 overflow-y-auto grid grid-cols-2 gap-1">
          {options.map(opt => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors select-none
                  ${checked ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50 text-gray-700'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt)}
                  className="h-4 w-4 text-blue-600 rounded flex-shrink-0"
                />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {selected.length > 0 ? `Selected: ${selected.join(', ')}` : 'None selected'}
        </p>
      </div>
    );
  };

// ─── Main component ───────────────────────────────────────────────────────────

interface EditRecordModalProps {
  database: DatabaseType;
  recordId: number;
  onClose: () => void;
  onSaved: () => void;
}

export const EditRecordModal: React.FC<EditRecordModalProps> = ({
  database,
  recordId,
  onClose,
  onSaved,
}) => {
  const [formData, setFormData]         = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [saveError, setSaveError]       = useState<string | null>(null);

  const fields = database === 'education' ? EDUCATION_FIELDS : ACTIONS_FIELDS;
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = database === 'education'
          ? await dataManagementApi.getEducationRecord(recordId)
          : await dataManagementApi.getActionsRecord(recordId);
        setFormData(data);
        setOriginalData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load record');
      } finally {
        setLoading(false);
      }
    })();
  }, [database, recordId]);

  const handleClose = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    onClose();
  };

  const handleChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setSaveError(null);
  };

  const handleSave = async () => {
    const missing = fields
      .filter(f => f.required && !formData[f.key]?.toString().trim())
      .map(f => f.label);
    if (missing.length > 0) {
      setSaveError(`Required fields missing: ${missing.join(', ')}`);
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      database === 'education'
        ? await dataManagementApi.updateEducationRecord(recordId, formData)
        : await dataManagementApi.updateActionsRecord(recordId, formData);
      onSaved();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  // ─── Field renderer ─────────────────────────────────────────────────────────

  const baseInputCls = `w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  const renderField = (field: FieldConfig) => {
    const val = formData[field.key] ?? '';

    switch (field.type) {
        case 'sdg_multiselect':
            return <SdgMultiSelect value={val} onChange={v => handleChange(field.key, v)} />;

        case 'select':
            return (
            <select
                value={val?.toString() ?? ''}
                onChange={e => handleChange(field.key, e.target.value)}
                className={baseInputCls}
            >
                {field.options?.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
            );

        case 'textarea':
            return (
            <>
                <textarea
                value={val}
                onChange={e => handleChange(field.key, e.target.value)}
                maxLength={field.maxLength}
                placeholder={field.placeholder}
                rows={4}
                className={`${baseInputCls} resize-y`}
                />
                {field.maxLength && (
                <p className="text-right text-xs text-gray-400 mt-0.5">
                    {val.toString().length}/{field.maxLength} characters
                </p>
                )}
            </>
            );

        case 'checkbox_multiselect':
            return (
              <CheckboxMultiSelect
                options={field.optionsList ?? []}
                value={val}
                onChange={v => handleChange(field.key, v)}
                separator={field.separator ?? '; '}
              />
            );

        default: // input
            return (
            <>
                <input
                type="text"
                value={val}
                onChange={e => handleChange(field.key, e.target.value)}
                maxLength={field.maxLength}
                placeholder={field.placeholder}
                className={baseInputCls}
                />
                {field.maxLength && (
                <p className="text-right text-xs text-gray-400 mt-0.5">
                    {val.toString().length}/{field.maxLength} characters
                </p>
                )}
            </>
            );
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Edit {database === 'education' ? 'Education' : 'Action'} Record
            </h2>
            <p className="text-sm text-gray-500">ID: {recordId}</p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                Unsaved changes
              </span>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              <span className="ml-3 text-gray-500">Loading record...</span>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-600 font-medium">{error}</p>
              <button onClick={onClose} className="mt-4 text-sm text-blue-600 hover:underline">Close</button>
            </div>
          ) : (
            <div className="space-y-5">
              {fields.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.hint && (
                    <p className="text-xs text-gray-400 mb-1">{field.hint}</p>
                  )}
                  {renderField(field)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex-shrink-0">
            {saveError && <p className="text-sm text-red-600 mb-3">{saveError}</p>}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300
                           rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg
                           hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};